import { Button, DatePicker } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { format, subDays } from "date-fns";
import { XIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";

export type PerformanceReportSearch = {
  dateFrom?: string;
  dateTo?: string;
  groupBy?: "day" | "week" | "month";
  status?: string;
  documentType?: string;
  view?: "overview" | "profitability";
  offset?: number;
};

type PerformanceReportDateFilterProps = {
  routeFrom:
    | "/app/performance/reports"
    | "/app/performance/reports/events"
    | "/app/performance/reports/payouts"
    | "/app/performance/reports/revenue"
    | "/app/performance/reports/requests"
    | "/app/performance/reports/performers";
  showGroupBy?: boolean;
  showEventStatus?: boolean;
  showPayoutStatus?: boolean;
  showDocumentType?: boolean;
  showRequestStatus?: boolean;
};

const GROUP_BY_OPTIONS = [
  { value: "day", label: "ລາຍວັນ" },
  { value: "week", label: "ລາຍອາທິດ" },
  { value: "month", label: "ລາຍເດືອນ" },
];

const EVENT_STATUS_OPTIONS = [
  { value: "all", label: "ທຸກສະຖານະ" },
  { value: "draft", label: "ຮ່າງ" },
  { value: "confirmed", label: "ຢືນຢັນແລ້ວ" },
  { value: "completed", label: "ສຳເລັດ" },
  { value: "cancelled", label: "ຍົກເລີກ" },
];

const PAYOUT_STATUS_OPTIONS = [
  { value: "all", label: "ທຸກສະຖານະ" },
  { value: "draft", label: "ຮ່າງ" },
  { value: "pending_approval", label: "ລໍຖ້າອະນຸມັດ" },
  { value: "approved", label: "ອະນຸມັດແລ້ວ" },
  { value: "paid", label: "ຈ່າຍແລ້ວ" },
  { value: "cancelled", label: "ຍົກເລີກ" },
];

const DOCUMENT_TYPE_OPTIONS = [
  { value: "all", label: "ທຸກປະເພດ" },
  { value: "quotation", label: "ໃບສະເໜີລາຄາ" },
  { value: "invoice", label: "ໃບເກັບເງິນ" },
  { value: "receipt", label: "ໃບຮັບເງິນ" },
];

const REQUEST_STATUS_OPTIONS = [
  { value: "all", label: "ທຸກສະຖານະ" },
  { value: "new", label: "ຮັບຄຳຂໍໃໝ່" },
  { value: "negotiating", label: "ກຳລັງເຈລະຈາ" },
  { value: "pending_quote", label: "ລໍອອກໃບສະເໜີລາຄາ" },
  { value: "won", label: "ຕົກລົງຈ້າງແລ້ວ" },
  { value: "lost", label: "ບໍ່ໄດ້ຮັບຈ້າງ" },
  { value: "cancelled", label: "ຍົກເລີກ" },
];

function toFilterValue(value: string) {
  return value === "all" ? undefined : value;
}

export function PerformanceReportDateFilter({
  routeFrom,
  showGroupBy = false,
  showEventStatus = false,
  showPayoutStatus = false,
  showDocumentType = false,
  showRequestStatus = false,
}: PerformanceReportDateFilterProps) {
  const nav = useNavigate({ from: routeFrom });
  const search = useSearch({ from: routeFrom }) as PerformanceReportSearch;

  const defaultFrom = format(subDays(new Date(), 30), "yyyy-MM-dd");
  const defaultTo = format(new Date(), "yyyy-MM-dd");

  const [fromDate, setFromDate] = useState(search.dateFrom ?? defaultFrom);
  const [toDate, setToDate] = useState(search.dateTo ?? defaultTo);
  const [groupBy, setGroupBy] = useState(search.groupBy ?? "day");
  const [status, setStatus] = useState(search.status ?? "all");
  const [documentType, setDocumentType] = useState(
    search.documentType ?? "all",
  );

  useEffect(() => {
    setFromDate(search.dateFrom ?? defaultFrom);
    setToDate(search.dateTo ?? defaultTo);
    setGroupBy(search.groupBy ?? "day");
    setStatus(search.status ?? "all");
    setDocumentType(search.documentType ?? "all");
  }, [
    search.dateFrom,
    search.dateTo,
    search.groupBy,
    search.status,
    search.documentType,
    defaultFrom,
    defaultTo,
  ]);

  const hasFilter =
    search.dateFrom ||
    search.dateTo ||
    search.groupBy ||
    search.status ||
    search.documentType;

  const applyFilters = (updates: Partial<PerformanceReportSearch>) => {
    nav({
      search: {
        dateFrom: updates.dateFrom ?? fromDate,
        dateTo: updates.dateTo ?? toDate,
        groupBy: showGroupBy ? (updates.groupBy ?? groupBy) : undefined,
        status:
          showEventStatus || showPayoutStatus || showRequestStatus
            ? toFilterValue(
                updates.status !== undefined ? updates.status : status,
              )
            : undefined,
        documentType: showDocumentType
          ? toFilterValue(
              updates.documentType !== undefined
                ? updates.documentType
                : documentType,
            )
          : undefined,
        offset: 0,
      },
    });
  };

  return (
    <div className="mb-4 flex flex-col gap-2 px-2">
      <div className="flex flex-wrap items-center gap-2">
        <DatePicker
          mode="range"
          value={{
            from: fromDate ? new Date(fromDate) : undefined,
            to: toDate ? new Date(toDate) : undefined,
          }}
          onChange={(range) => {
            const from: Date | undefined = Array.isArray(range)
              ? range[0]
              : (range as { from?: Date; to?: Date })?.from;
            const to: Date | undefined = Array.isArray(range)
              ? range[1]
              : (range as { from?: Date; to?: Date })?.to;
            const fromStr = from ? format(from, "yyyy-MM-dd") : defaultFrom;
            const toStr = to ? format(to, "yyyy-MM-dd") : defaultTo;
            setFromDate(fromStr);
            setToDate(toStr);
            applyFilters({ dateFrom: fromStr, dateTo: toStr });
          }}
          placeholder="ຊ່ວງວັນທີ"
          className="h-8 w-full sm:w-fit"
        />

        {showGroupBy && (
          <SimpleSelect
            placeholder="ຈັດກຸ່ມ"
            value={groupBy}
            onValueChange={(v) => {
              setGroupBy(v as "day" | "week" | "month");
              applyFilters({ groupBy: v as "day" | "week" | "month" });
            }}
            options={GROUP_BY_OPTIONS}
            className="h-8 w-full sm:w-36"
          />
        )}

        {showEventStatus && (
          <SimpleSelect
            placeholder="ສະຖານະງານ"
            value={status}
            onValueChange={(v) => {
              setStatus(v);
              applyFilters({ status: v });
            }}
            options={EVENT_STATUS_OPTIONS}
            className="h-8 w-full sm:w-40"
          />
        )}

        {showPayoutStatus && (
          <SimpleSelect
            placeholder="ສະຖານະໃບເບີກຈ່າຍ"
            value={status}
            onValueChange={(v) => {
              setStatus(v);
              applyFilters({ status: v });
            }}
            options={PAYOUT_STATUS_OPTIONS}
            className="h-8 w-full sm:w-44"
          />
        )}

        {showDocumentType && (
          <SimpleSelect
            placeholder="ປະເພດເອກະສານ"
            value={documentType}
            onValueChange={(v) => {
              setDocumentType(v);
              applyFilters({ documentType: v });
            }}
            options={DOCUMENT_TYPE_OPTIONS}
            className="h-8 w-full sm:w-40"
          />
        )}

        {showRequestStatus && (
          <SimpleSelect
            placeholder="ສະຖານະຄຳຂໍ"
            value={status}
            onValueChange={(v) => {
              setStatus(v);
              applyFilters({ status: v });
            }}
            options={REQUEST_STATUS_OPTIONS}
            className="h-8 w-full sm:w-44"
          />
        )}

        {hasFilter && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setFromDate(defaultFrom);
              setToDate(defaultTo);
              setGroupBy("day");
              setStatus("all");
              setDocumentType("all");
              nav({
                search: {
                  dateFrom: defaultFrom,
                  dateTo: defaultTo,
                  groupBy: showGroupBy ? "day" : undefined,
                  status: undefined,
                  documentType: undefined,
                  offset: 0,
                },
              });
            }}
          >
            <XIcon className="mr-1 size-4" />
            ລ້າງ
          </Button>
        )}
      </div>
    </div>
  );
}

export function usePerformanceReportDates(search: PerformanceReportSearch) {
  const defaultFrom = format(subDays(new Date(), 30), "yyyy-MM-dd");
  const defaultTo = format(new Date(), "yyyy-MM-dd");
  return {
    dateFrom: search.dateFrom ?? defaultFrom,
    dateTo: search.dateTo ?? defaultTo,
    groupBy: search.groupBy ?? "day",
  };
}
